import { IconSearch, IconX } from "@tabler/icons-react"
import { type FC, useMemo, useState } from "react"

import { getLandingFeatureIconValue, landingFeatureIcons } from "@forento/core/models/landingFeatureIcon"
import LandingFeatureIcon from "@forento/ui/components/LandingFeatureIcon"
import { cn } from "@forento/ui/utilities/tailwind"

import { DefaultModal } from "~/components/Modal"

type Props = {
	isOpen: boolean
	selectedValue?: string
	onSelect: (value: string) => void
	onClose: () => void
}
const FeatureIconPickerModal: FC<Props> = ({ isOpen, selectedValue, onSelect, onClose }) => {
	const [query, setQuery] = useState("")
	const normalizedQuery = query.trim().toLowerCase()
	const filteredIcons = useMemo(
		() =>
			landingFeatureIcons.filter(icon =>
				`${icon.label} ${icon.keywords}`.toLowerCase().includes(normalizedQuery),
			),
		[normalizedQuery],
	)

	const close = () => {
		setQuery("")
		onClose()
	}

	return (
		<DefaultModal onClose={close} width={800} isOpen={isOpen}>
			<div className="flex items-start justify-between gap-6 px-8 pb-5">
				<div>
					<h2 className="text-xl font-semibold text-gray-900">Choose an icon</h2>
					<p className="mt-1 text-sm text-gray-500">Select a Tabler icon for this feature.</p>
				</div>
				<button
					type="button"
					onClick={close}
					aria-label="Close icon picker"
					className="cursor-pointer rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
				>
					<IconX className="size-5" />
				</button>
			</div>
			<div className="px-8">
				<label className="relative block">
					<span className="sr-only">Search icons</span>
					<IconSearch className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-gray-400" />
					<input
						type="search"
						value={query}
						onChange={event => setQuery(event.target.value)}
						placeholder="Search icons"
						className="focus:border-primary focus:ring-primary/15 h-11 w-full rounded-lg border border-gray-200 bg-white pr-4 pl-10 text-sm text-gray-900 transition-shadow outline-none placeholder:text-gray-400 focus:ring-2"
					/>
				</label>
				<div className="mt-5 max-h-115 overflow-y-auto pr-1">
					{filteredIcons.length > 0 ? (
						<div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
							{filteredIcons.map(icon => {
								const value = getLandingFeatureIconValue(icon.id)
								const isSelected = value === selectedValue

								return (
									<button
										type="button"
										key={icon.id}
										onClick={() => {
											onSelect(value)
											close()
										}}
										aria-pressed={isSelected}
										className={cn(
											"flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border p-3 text-center transition-colors",
											isSelected
												? "border-primary bg-primary/5 text-primary ring-primary ring-1"
												: "hover:border-primary/40 border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900",
										)}
									>
										<LandingFeatureIcon value={value} className="size-7" />
										<span className="line-clamp-2 text-xs leading-4">{icon.label}</span>
									</button>
								)
							})}
						</div>
					) : (
						<div className="flex min-h-48 flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-center">
							<IconSearch className="size-7 text-gray-400" />
							<p className="mt-3 text-sm font-medium text-gray-700">No icons found</p>
							<p className="mt-1 text-xs text-gray-500">Try a different search term.</p>
						</div>
					)}
				</div>
			</div>
		</DefaultModal>
	)
}

export default FeatureIconPickerModal
